import {
  AdminDollarInput2,
  AdminInput,
  AdminSwitch,
  ArrowRed,
  Box,
  Button,
  Callout,
  Disclaimer,
  Div,
  ErrorToast,
  GrubconLogoWhiteSimbol,
  H3,
  H5,
  Icon,
  Modal,
  P,
  PaymentTabletIcon,
  PrinterIcon,
  Separator,
  SuccessToast,
  VDriveLogoSmall,
  VGrubsLogoSmall,
  VTabletLogoSmall,
  api,
  displayCentsInDollars,
  displayCentsInDollarsShort,
  getLocationById,
  lightTheme,
} from '@vgrubs/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EditServiceFee } from './EditServiceFee';
import {
  PER_DAY_FEE_MAX,
  PER_DAY_FEE_MIN,
  PER_ORDER_FEE_MAX,
  PER_ORDER_FEE_MIN,
  REVENUE_RECOVERY_FEE,
  billingModeFor,
  servicesMonthlyTotal,
} from './servicesTotal';
import { VTabletBilling } from './VTabletBilling';

export const EditServices = ({
  salesConfig,
  modalIsOpen,
  setModalIsOpen,
  preferences,
  id,
  setLoading,
  setData,
  componentText,
  onServicesUpdated,
}) => {
  const [serviceFeeModal, setServiceFeeModal] = useState(false);

  const [vDriveService, setVDriveService] = useState(false);
  const [vOrdersService, setVOrdersService] = useState(false);
  const [vGrubsService, setVGrubsService] = useState(false);
  const [vRevenueService, setVRevenueService] = useState(false);

  const [vDriveDeliveryCharge, setVDriveDeliveryCharge] = useState(0);
  const [extraPrinters, setExtraPrinters] = useState('');
  const [extraTablets, setExtraTablets] = useState('');

  const [vOrdersMonthlySalesFee, setVOrdersMonthlySalesFee] = useState(null);
  // How vTablet is billed ('monthly', 'perOrder', 'perDay'), and the cents
  // per order and per day (0 while nothing is entered).
  const [billing, setBilling] = useState('monthly');
  const [perOrderFee, setPerOrderFee] = useState(0);
  const [perDayFee, setPerDayFee] = useState(0);

  const [totalAmount, setTotalAmount] = useState(0);

  // vTablet was off and only came on because vDrive needs it.
  const vTabletCameWithVDrive = useRef(false);

  const revenueRecoveryFee = vRevenueService ? REVENUE_RECOVERY_FEE : 0;

  useEffect(() => {
    if (salesConfig) {
      setVDriveService(salesConfig.vDriveService);
      setVOrdersService(salesConfig.vOrdersService);
      setVGrubsService(salesConfig.vGrubsService);
      setVRevenueService(salesConfig.vRevenueService);
      setTotalAmount(salesConfig.totalAmount);
      if (salesConfig.vDriveService) {
        setVDriveDeliveryCharge(salesConfig.vDriveDeliveryCharge);
      }
      if (salesConfig.vOrdersService) {
        setVOrdersMonthlySalesFee(salesConfig.vOrdersMonthlySalesFee);
      }
      setBilling(billingModeFor(salesConfig));
      setPerOrderFee(salesConfig.vOrdersPerOrderFee ?? 0);
      setPerDayFee(salesConfig.vOrdersPerDayFee ?? 0);
      // if (salesConfig.vGrubsService) {
      setExtraPrinters(salesConfig.extraPrinters);
      setExtraTablets(salesConfig.extraTablets);
      // }
    }
    vTabletCameWithVDrive.current = false;
  }, [modalIsOpen]);

  const handleVDriveDeliveryCharge = useCallback((val) => {
    setVDriveDeliveryCharge(val);
  }, []);

  const handleTotalAmount = useCallback((val) => {
    setTotalAmount(val);
  }, []);

  async function handleEditServices() {
    if (Number(extraPrinters) > Number(extraTablets))
      return ErrorToast(
        `You can't select more printers than tablets. Please adjust your selection.`
      );

    if (vDriveService && !vOrdersService)
      return ErrorToast('vTablet service is required to use vDrive service.');

    if (vOrdersService && !vOrdersMonthlySalesFee)
      return ErrorToast('Please select a service fee to enable vTablet.');

    const nextPerOrderFee =
      vOrdersService && billing === 'perOrder' ? perOrderFee : null;
    const nextPerDayFee =
      vOrdersService && billing === 'perDay' ? perDayFee : null;

    if (
      nextPerOrderFee != null &&
      (nextPerOrderFee < PER_ORDER_FEE_MIN ||
        nextPerOrderFee > PER_ORDER_FEE_MAX)
    )
      return ErrorToast('Enter an amount per order between $0.01 and $5.00.');

    if (
      nextPerDayFee != null &&
      (nextPerDayFee < PER_DAY_FEE_MIN || nextPerDayFee > PER_DAY_FEE_MAX)
    )
      return ErrorToast('Enter an amount per day between $0.01 and $5.00.');

    const body = {
      monthlyFeeId: vOrdersMonthlySalesFee?.id ?? -1,
      vDriveDeliveryChargeFee: vDriveService ? vDriveDeliveryCharge : -1,
      vGrubsService,
      extraTablets: Number(extraTablets) ?? null,
      extraPrinters: Number(extraPrinters) ?? null,
      totalAmount,
      vRevenueService,
      revenueRecoveryFee,
    };

    // An unchanged fee is left out. No fee is always sent as -1: a location
    // without a sales config needs vTablet turned off explicitly.
    if (
      vOrdersMonthlySalesFee &&
      vOrdersMonthlySalesFee.id === salesConfig?.vOrdersMonthlySalesFee?.id
    )
      delete body.monthlyFeeId;

    // Each left out = unchanged, -1 = off. Turning vTablet off needs
    // neither: `monthlyFeeId: -1` clears both on the backend. Moving from
    // one to the other sends the old one as -1: both set is a 400.
    if (nextPerOrderFee !== (salesConfig?.vOrdersPerOrderFee ?? null)) {
      if (nextPerOrderFee != null) body.vOrdersPerOrderFee = nextPerOrderFee;
      else if (vOrdersService) body.vOrdersPerOrderFee = -1;
    }
    if (nextPerDayFee !== (salesConfig?.vOrdersPerDayFee ?? null)) {
      if (nextPerDayFee != null) body.vOrdersPerDayFee = nextPerDayFee;
      else if (vOrdersService) body.vOrdersPerDayFee = -1;
    }

    try {
      setLoading(true);
      const { data } = await api.put(`/v1/locations/${id}/sales-config`, body);
      SuccessToast('Services are successfully updated!');
      setModalIsOpen(false);

      setData((prev) => {
        return {
          ...prev,
          salesConfig: {
            ...prev.salesConfig,
            vGrubsService,
            vDriveService,
            vOrdersService,
            vOrdersMonthlySalesFee,
            vOrdersPerOrderFee: nextPerOrderFee,
            vOrdersPerDayFee: nextPerDayFee,
            extraPrinters,
            extraTablets,
            vDriveDeliveryCharge,
            totalAmount,
            vRevenueService,
            revenueRecoveryFee,
          },
        };
      });

      // Editing services re-splits/empties the per-service agent lists on the
      // backend, so refresh the agents/earning-plans data.
      onServicesUpdated?.();

      // The backend fills an empty total itself when Revenue Recovery is the
      // only service, so show what it saved rather than what was sent.
      refreshSalesConfig();
    } catch (error) {
      ErrorToast(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  async function refreshSalesConfig() {
    try {
      const now = new Date();
      const { data } = await getLocationById(id, { from: now, to: now });
      const saved = data.data?.salesConfig;
      if (saved)
        setData((prev) => ({
          ...prev,
          salesConfig: { ...prev.salesConfig, ...saved },
        }));
    } catch {
      // The save went through; the total shown is just the one that was sent.
    }
  }

  const handleChangeDeliveryFee = (v) => {
    if (!vDriveService) setVDriveService(true);
    handleVDriveDeliveryCharge(v);
  };

  const handleChangeServiceFee = (v) => {
    if (!vOrdersService) setVOrdersService(true);
    setVOrdersMonthlySalesFee((prev) => {
      const newFee = v?.fee;
      const prevFee = prev?.fee;

      // Billed per order or per day, the monthly fee is not part of the
      // total, so picking another one (or none) leaves the total where it is.
      if (billing !== 'monthly') return v;

      if (prev) {
        if (newFee) {
          setTotalAmount((currentAmount) => {
            return currentAmount - prevFee + newFee;
          });
        } else {
          setTotalAmount((currentAmount) => {
            const deducted = currentAmount - prevFee;
            return deducted < 0 ? 0 : deducted;
          });
        }
      } else {
        if (newFee) {
          setTotalAmount((currentAmount) => {
            return currentAmount + newFee;
          });
        }
      }

      return v;
    });
  };

  const extraTabletPrice = preferences.extraTabletPrice;
  const extraPrinterPrice = preferences.extraPrinterPrice;

  const handleChangeTablets = (e) => {
    let value = e.target.value;
    // Allow only numbers
    if (!/^\d*$/.test(value)) return;
    // Prevent leading zeros (but allow single "0")
    if (value.length > 1 && value.startsWith('0')) return;
    // Update state or handle value

    setExtraTablets((prev) => {
      const newTablets = Number(value);
      const prevTablets = Number(prev);

      const newAmount = newTablets * extraTabletPrice;
      const prevAmount = prevTablets * extraTabletPrice;

      if (prevTablets > 0) {
        if (newTablets > 0) {
          setTotalAmount((currentAmount) => {
            return currentAmount - prevAmount + newAmount;
          });
        } else {
          setTotalAmount((currentAmount) => {
            const deducted = currentAmount - prevAmount;
            return deducted < 0 ? 0 : deducted;
          });
        }
      } else {
        setTotalAmount((currentAmount) => {
          return currentAmount + newAmount;
        });
      }

      return value;
    });
  };

  const handleChangePrinters = (e) => {
    let value = e.target.value;
    // Allow only numbers
    if (!/^\d*$/.test(value)) return;
    // Prevent leading zeros (but allow single "0")
    if (value.length > 1 && value.startsWith('0')) return;
    // Update state or handle value

    setExtraPrinters((prev) => {
      const newTablets = Number(value);
      const prevTablets = Number(prev);

      const newAmount = newTablets * extraPrinterPrice;
      const prevAmount = prevTablets * extraPrinterPrice;

      if (prevTablets > 0) {
        if (newTablets > 0) {
          setTotalAmount((currentAmount) => {
            return currentAmount - prevAmount + newAmount;
          });
        } else {
          setTotalAmount((currentAmount) => {
            const deducted = currentAmount - prevAmount;
            return deducted < 0 ? 0 : deducted;
          });
        }
      } else {
        setTotalAmount((currentAmount) => {
          return currentAmount + newAmount;
        });
      }

      return value;
    });
  };

  const handleChangeVDriveService = (v) => {
    setVDriveService(v);
    setVDriveDeliveryCharge(0);

    // vDrive needs vTablet: clearing the fee (the list depends on vDrive)
    // turns vTablet on too.
    if (v) {
      vTabletCameWithVDrive.current = !vOrdersService;
      handleChangeServiceFee(null);
      return;
    }

    // Turning vDrive off puts back a vTablet that only came along with it,
    // as long as no fee was picked for it since - otherwise it stayed on
    // without a fee and nothing else (Revenue Recovery alone) could be saved.
    if (vTabletCameWithVDrive.current && !vOrdersMonthlySalesFee)
      handleChangeVOrderService(false);
    else if (vOrdersService) handleChangeServiceFee(null);
    vTabletCameWithVDrive.current = false;
  };

  const handleChangeVOrderService = (v) => {
    setVOrdersService(v);
    vTabletCameWithVDrive.current = false;

    if (v) {
      setServiceFeeModal(true);
    } else {
      handleChangeServiceFee(null);
      // The backend drops per-order and per-day billing with vTablet; mirror
      // it so turning vTablet back on starts from monthly.
      setBilling('monthly');
      setPerOrderFee(0);
      setPerDayFee(0);
    }
  };

  const servicesForTotal = {
    vTabletFee: vOrdersMonthlySalesFee?.fee,
    extraTablets,
    extraTabletPrice,
    extraPrinters,
    extraPrinterPrice,
    vRevenueService,
  };

  const handleChangeBilling = (nextBilling) => {
    if (nextBilling === billing) return;
    // A total that is still just the services added up follows the switch,
    // the way it follows every toggle here. One typed in by hand is left
    // alone, as the backend leaves it - the callout above Total Amount asks
    // the admin to lower it instead.
    if (totalAmount === servicesMonthlyTotal({ ...servicesForTotal, billing }))
      setTotalAmount(
        servicesMonthlyTotal({ ...servicesForTotal, billing: nextBilling })
      );
    setBilling(nextBilling);
  };

  const notMonthly = vOrdersService && billing !== 'monthly';
  const totalWithoutVTablet = servicesMonthlyTotal({
    ...servicesForTotal,
    billing: 'perOrder',
  });
  // Higher than the services add up to without the monthly vTablet fee:
  // most likely a hand-typed total that still charges it.
  const totalStillHasVTabletFee =
    notMonthly && totalAmount > totalWithoutVTablet;

  // Revenue Recovery alone with no total: the backend sets one on save.
  const backendFillsTotal =
    vRevenueService &&
    !vOrdersService &&
    !vDriveService &&
    !vGrubsService &&
    !totalAmount;

  const handleChangeVRevenueService = (v) => {
    setVRevenueService(v);

    setTotalAmount((prev) => {
      const fee = REVENUE_RECOVERY_FEE;

      if (v) {
        return prev + fee;
      } else {
        const deducted = prev - fee;
        return deducted < 0 ? 0 : deducted;
      }
    });
  };

  const handleChangeVGrubsService = (v) => {
    setVGrubsService(v);
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        closeModal={() => setModalIsOpen(false)}
        header={componentText}
        width="1000px"
        logo={false}
        textAlign="left"
      >
        <Div $flex $gap="50px" $gapSM="24px" $columnSM>
          <Div $width="50%" $widthSM="100%">
            <SwitchComp
              Logo={VDriveLogoSmall}
              text="vDrive"
              state={vDriveService}
              setState={handleChangeVDriveService}
            />
            <Box $p="16px 20px">
              <Div $flex $alignCenter $between>
                <H5 $m="0">Delivery Fee</H5>
                <Div>
                  <H3 $right $m="0">
                    <span>
                      {displayCentsInDollars(725 - vDriveDeliveryCharge)}
                    </span>
                  </H3>
                  <Disclaimer $color={lightTheme.lightGray} $right $m="0">
                    Per Delivery
                  </Disclaimer>
                </Div>
              </Div>
              <Separator $m="16px 0" />
              <P $m="0" $color={lightTheme.lightGray}>
                This delivery fee doesn’t factor in the savings that you get
                from decreasing your delivery app commission by 15%
              </P>
              <Separator $m="16px 0" />
              <Div $flex $alignCenter $between $gap="20px">
                <P $m="0">How much you want to charge Diner for delivery?</P>
                <Div $width="100px" $flexNone>
                  <AdminDollarInput2
                    onValueChange={handleChangeDeliveryFee}
                    value={vDriveDeliveryCharge ?? 0}
                    $textCenter
                    max={725}
                  />
                </Div>
              </Div>
            </Box>
          </Div>
          <Div $width="50%" $widthSM="100%" $flex $column $between>
            <Div>
              <SwitchComp
                Logo={VGrubsLogoSmall}
                text="vGrubs"
                state={vGrubsService}
                setState={handleChangeVGrubsService}
              />

              <Box $p="16px 20px" $mb="20">
                <Div $flex $alignCenter $between $gap="10px">
                  <Div $flex $alignCenter $gap="10px">
                    <PaymentTabletIcon width="30" height="30" />
                    <H5 $m="0">Additional Tablets</H5>
                  </Div>

                  <Div>
                    <H3 $right $m="0">
                      <span>
                        {displayCentsInDollars(extraTabletPrice * extraTablets)}
                      </span>
                    </H3>
                    <Disclaimer $color={lightTheme.lightGray} $right $m="0">
                      Per Month
                    </Disclaimer>
                  </Div>
                </Div>

                <Separator $m="16px 0" />
                <Div $flex $alignCenter $between $gap="10px">
                  <Div>
                    <P $m="0">Do you need extra tablets?</P>
                    <P $color={lightTheme.lightGray} $m="0">
                      Additional one is{' '}
                      {displayCentsInDollarsShort(extraTabletPrice)}
                      /mo.
                    </P>
                  </Div>
                  <Div $width="100px" $flexNone>
                    <AdminInput
                      value={extraTablets}
                      onChange={handleChangeTablets}
                      $textCenter
                      type="number"
                      placeholder="0"
                    />
                  </Div>
                </Div>
              </Box>

              <Box $p="16px 20px">
                <Div $flex $alignCenter $between $gap="10px">
                  <Div $flex $alignCenter $gap="10px">
                    <PrinterIcon width="30" height="30" />
                    <H5 $m="0">Additional Printers</H5>
                  </Div>

                  <Div>
                    <H3 $right $m="0">
                      <span>
                        {displayCentsInDollars(
                          extraPrinterPrice * extraPrinters
                        )}
                      </span>
                    </H3>
                    <Disclaimer $color={lightTheme.lightGray} $right $m="0">
                      Per Month
                    </Disclaimer>
                  </Div>
                </Div>

                <Separator $m="16px 0" />
                <Div $flex $alignCenter $between $gap="10px">
                  <Div>
                    <P $m="0">Do you need extra printers?</P>
                    <P $color={lightTheme.lightGray} $m="0">
                      Additional one is{' '}
                      {displayCentsInDollarsShort(extraPrinterPrice)}
                      /mo.
                    </P>
                  </Div>
                  <Div $width="100px" $flexNone>
                    <AdminInput
                      value={extraPrinters}
                      onChange={handleChangePrinters}
                      $textCenter
                      type="number"
                      placeholder="0"
                    />
                  </Div>
                </Div>
              </Box>
            </Div>
          </Div>
        </Div>

        <Separator $m="20px 0" $color={lightTheme.lightGray} />

        {/* Side by side like vDrive and vGrubs above: the vTablet card grows
            with per-order billing, and as a row of its own it stretched the
            whole row past its logo. */}
        <Div $flex $gap="50px" $gapSM="24px" $columnSM>
          <Div $width="50%" $widthSM="100%">
            <SwitchComp
              Logo={VTabletLogoSmall}
              text="vTablet"
              state={vOrdersService}
              setState={handleChangeVOrderService}
            />
            <Box $p="16px 20px">
              <Div
                $flex
                $alignCenter
                $between
                $pointer
                onClick={() => setServiceFeeModal(true)}
              >
                <Div>
                  <H5 $m="0">Service Fee</H5>
                  <P $m="0">
                    {billing === 'perOrder'
                      ? 'Target per month'
                      : billing === 'perDay'
                      ? 'Most per month'
                      : 'Per Month'}
                  </P>
                </Div>
                <Div $flex $alignCenter $gap="12px">
                  <Div $width="120px" $flexNone>
                    {vOrdersMonthlySalesFee && (
                      <Div
                        $outlineBox
                        $flex
                        $center
                        $height="45px"
                        $heightSM="45px"
                        $borderRadius="100px"
                      >
                        <H3 $m="0">
                          <span>
                            {displayCentsInDollarsShort(
                              vOrdersMonthlySalesFee.fee
                            )}
                          </span>
                        </H3>
                      </Div>
                    )}
                  </Div>
                  <Icon>
                    <ArrowRed />
                  </Icon>
                </Div>
              </Div>
              {vOrdersService && (
                <VTabletBilling
                  locationId={id}
                  monthlyTarget={vOrdersMonthlySalesFee?.fee}
                  billing={billing}
                  onBillingChange={handleChangeBilling}
                  perOrderFee={perOrderFee}
                  onPerOrderFeeChange={setPerOrderFee}
                  perDayFee={perDayFee}
                  onPerDayFeeChange={setPerDayFee}
                />
              )}
            </Box>
          </Div>
          <Div $width="50%" $widthSM="100%">
            <SwitchComp
              Logo={GrubconLogoWhiteSimbol}
              text="Revenue Recovery"
              state={vRevenueService}
              setState={handleChangeVRevenueService}
            />
            <Box $p="16px 20px">
              <Div $flex $alignCenter $between>
                <Div>
                  <H5 $m="0">Service Fee</H5>
                  <P $m="0">Per Month</P>
                </Div>
                <Div $flex $alignCenter $gap="12px">
                  <Div $width="120px" $flexNone>
                    <H3 $m="0" $right>
                      <span>
                        {displayCentsInDollarsShort(REVENUE_RECOVERY_FEE)}
                      </span>
                    </H3>
                  </Div>
                </Div>
              </Div>
            </Box>
          </Div>
        </Div>

        <Separator $m="20px 0" $color={lightTheme.lightGray} />

        {totalStillHasVTabletFee && (
          <Div $mb="16">
            <Callout tone="warning">
              Total Amount is set manually. Lower it - the monthly vTablet fee
              is now charged {billing === 'perDay' ? 'per day' : 'per order'}.
              The other services add up to{' '}
              {displayCentsInDollars(totalWithoutVTablet)}.
            </Callout>
          </Div>
        )}

        <Div $flex $between $alignCenter $columnSM $gap="12px">
          <Div>
            <H5 $m="0" $centerSM>
              Total Amount
            </H5>
            <P $m="0" $centerSM>
              Monthly Fee
            </P>
          </Div>
          <Div $flex $alignCenter $columnSM $gap="16px">
            <Disclaimer $m="0" $right $centerSM>
              SaaS Fee is customizable. <br /> To adjust it, change the total
              amount.
            </Disclaimer>

            <Div $width="150px" $flexNone>
              <AdminDollarInput2
                onValueChange={handleTotalAmount}
                value={totalAmount}
                $textCenter
                $bigRed
              />
            </Div>
          </Div>
        </Div>
        {backendFillsTotal && (
          <Disclaimer
            $m="8px 0 0"
            $right
            $centerSM
            $color={lightTheme.lightGray}
          >
            Total will be set to the vRevenue fee + extras (
            {displayCentsInDollars(
              servicesMonthlyTotal({
                ...servicesForTotal,
                vTabletFee: null,
                billing: 'monthly',
              })
            )}
            )
          </Disclaimer>
        )}
        <Div $flex $gap="16px" $mt="32" $columnSM>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setModalIsOpen(false)}
          >
            Cancel
          </Button>
          <Button $selected $w="100%" onClick={handleEditServices}>
            Save
          </Button>
        </Div>
      </Modal>
      <EditServiceFee
        modalIsOpen={serviceFeeModal}
        setModalIsOpen={setServiceFeeModal}
        vOrdersMonthlySalesFee={vOrdersMonthlySalesFee}
        setVOrdersMonthlySalesFee={setVOrdersMonthlySalesFee}
        vDriveService={vDriveService}
        billing={vOrdersService ? billing : 'monthly'}
        preferences={preferences}
        handleChangeServiceFee={handleChangeServiceFee}
      />
    </>
  );
};

const SwitchComp = ({ state, setState, Logo, text, mb = '16' }) => {
  return (
    <Div $flex $alignCenter $between $mb={mb}>
      <Div $flex $alignCenter $gap="20px" $gapSM="10px">
        <Div $desktop>
          <Logo width="75" height="75" />
        </Div>
        <Div $mobile>
          <Logo width="50" height="50" />
        </Div>

        <H3 $m="0">{text}</H3>
      </Div>
      <AdminSwitch
        checked={state}
        onChange={(e) => setState(e.target.checked)}
      />
    </Div>
  );
};
